import { useEffect, useState } from "react";
import { Loader2, Download, FileSpreadsheet, Table2 } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtErr } from "../api";

const pad = (n) => String(n).padStart(2, "0");
const now = new Date();
const TODAY = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
const MONTH = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;

const Reports = () => {
  const [date, setDate] = useState(TODAY);
  const [month, setMonth] = useState(MONTH);
  const [range, setRange] = useState({ start: TODAY, end: TODAY });
  const [busy, setBusy] = useState("");
  const [section, setSection] = useState("");
  const [departments, setDepartments] = useState([]);
  const [preview, setPreview] = useState(null); // inline daily report rows
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    staffApi.get("/departments").then((r) => setDepartments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const withSection = (params) => (section ? { ...params, section } : params);

  const download = async (key, path, params, filename) => {
    setBusy(key);
    try {
      const res = await staffApi.get(path, { params: withSection(params), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  // Inline daily preview — renders whatever columns the backend report contains
  // (extended fields like first-in/final-out/totals/source flow through automatically).
  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await staffApi.get("/reports/daily", { params: withSection({ date }) });
      const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : Array.isArray(data?.items) ? data.items : null;
      setPreview(rows || []);
      if (!rows) toast.message("Daily report returned an unexpected shape.", { description: "Downloads still work — the inline preview expects a row list." });
    } catch (e) {
      toast.error(fmtErr(e));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const DlBtn = ({ id, label, onClick }) => (
    <button onClick={onClick} disabled={!!busy} data-testid={id}
      className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] hover:text-[#C5221F] disabled:opacity-50 text-slate-700 px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
      {busy === id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {label}
    </button>
  );

  return (
    <div data-testid="admin-reports">
      <h1 className="font-display text-2xl font-bold text-slate-900">Reports</h1>
      <p className="mt-1 text-sm text-slate-500">Attendance reports download as Excel/CSV, generated live by the attendance system.</p>
      <div className="mt-5 flex items-center gap-3">
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Department / Section</label>
        <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="report-section"
          className="border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]">
          <option value="">All</option>
          {departments.map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
        </select>
      </div>
      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="bg-white border border-slate-200 p-6" data-testid="report-daily-card">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <FileSpreadsheet size={14} className="text-[#C5221F]" /> Daily Report
          </p>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-daily-date"
            className="mt-4 w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
          <div className="mt-4 flex flex-wrap gap-2">
            <DlBtn id="report-daily-xlsx" label="Excel" onClick={() => download("report-daily-xlsx", "/reports/daily.xlsx", { date }, `daily-report-${date}.xlsx`)} />
            <button onClick={loadPreview} disabled={previewLoading} data-testid="report-daily-view"
              className="inline-flex items-center gap-1.5 bg-[#0A1128] hover:bg-[#16204a] disabled:opacity-50 text-white px-4 py-2 text-[11px] font-display font-semibold uppercase tracking-widest rounded-sm transition-colors">
              {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Table2 size={12} />} View
            </button>
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="report-monthly-card">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <FileSpreadsheet size={14} className="text-[#C5221F]" /> Monthly Report
          </p>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input"
            className="mt-4 w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
          <div className="mt-4 flex flex-wrap gap-2">
            <DlBtn id="report-monthly-xlsx" label="Excel" onClick={() => download("report-monthly-xlsx", "/reports/monthly.xlsx", { month }, `monthly-report-${month}.xlsx`)} />
            <DlBtn id="report-monthly-csv" label="CSV" onClick={() => download("report-monthly-csv", "/reports/monthly.csv", { month }, `monthly-report-${month}.csv`)} />
          </div>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="report-range-card">
          <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
            <FileSpreadsheet size={14} className="text-[#C5221F]" /> Date Range
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-range-start"
              className="border border-slate-300 px-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
            <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-range-end"
              className="border border-slate-300 px-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <DlBtn id="report-range-xlsx" label="Excel" onClick={() => download("report-range-xlsx", "/reports/range.xlsx", range, `report-${range.start}-to-${range.end}.xlsx`)} />
            <DlBtn id="report-range-csv" label="CSV" onClick={() => download("report-range-csv", "/reports/range.csv", range, `report-${range.start}-to-${range.end}.csv`)} />
          </div>
        </div>
      </div>

      {/* inline daily preview — columns come straight from the backend report rows */}
      {preview && (
        <div className="mt-6 bg-white border border-slate-200 overflow-x-auto" data-testid="report-preview">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Daily Report Preview — {date}{section ? ` · ${section}` : ""}</p>
            <button onClick={() => setPreview(null)} data-testid="report-preview-close" className="text-[11px] font-semibold text-slate-500 hover:text-[#C5221F]">Close</button>
          </div>
          {preview.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500" data-testid="report-preview-empty">No rows for this date.</p>
          ) : (
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  {Object.keys(preview[0]).map((k) => (
                    <th key={k} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{k.replace(/_/g, " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.map((row, i) => (
                  <tr key={row.id || row.user_id || i} data-testid={`report-preview-row-${i}`}>
                    {Object.keys(preview[0]).map((k) => (
                      <td key={k} className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{row[k] == null || row[k] === "" ? "—" : String(row[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
